/**
 * Canonical href resolution for shell chrome (headers, sidebars,
 * footers, menus).
 *
 * <p>Operator-authored menu links come from the shell config as free
 * text, so a fair share of them are stored WITHOUT a leading slash
 * ({@code "blog"}, {@code "faq"}, {@code "about"}). A bare href is
 * document-relative: it works from {@code /} but resolves against the
 * current directory everywhere else, so the same sidebar link points at
 * {@code /blog} on the home page and {@code /lxp/blog} under
 * {@code /lxp}. Chrome links are always site-root paths — normalise the
 * stored value instead of trusting it.
 *
 * <p>Left alone: absolute URLs, protocol-relative URLs, non-http
 * schemes ({@code mailto:}, {@code tel:}), and in-page targets
 * ({@code #anchor}, {@code ?q=1}).
 */

/** True when the value addresses something other than a site path. */
function isExternalOrInPage(href: string): boolean {
  return (
    href.startsWith('//') ||
    href.startsWith('#') ||
    href.startsWith('?') ||
    /^[a-z][a-z0-9+.-]*:/i.test(href)
  );
}

/**
 * Force a stored chrome href to a site-root path.
 *
 * {@code "blog"} → {@code "/blog"}; {@code "/blog"}, {@code "#top"},
 * {@code "https://…"} and {@code "mailto:…"} pass through unchanged.
 */
export function normaliseShellHref(href: string): string {
  if (!href) return href;
  if (href.startsWith('/') || isExternalOrInPage(href)) return href;
  return `/${href}`;
}

/**
 * Shell-wide href resolver: normalise, then apply the tenant prefix for
 * hosts that serve tenants under a path segment. Hosts without such
 * prefixes pass {@code tenantSlug: ''} and get the normalised path back.
 *
 * @param href       stored menu href (may be undefined / relative)
 * @param tenantSlug tenant path segment, or {@code ''} for none
 * @returns a navigable href; {@code '#'} when there is nothing to link to
 */
export function shellHref(href: string | undefined, tenantSlug: string): string {
  if (!href) return '#';
  if (isExternalOrInPage(href)) return href;
  const path = normaliseShellHref(href);
  // No tenant segment: returning `/${''}${path}` would emit `//path`,
  // which the browser reads as protocol-relative and navigates off-site.
  if (!tenantSlug) return path;
  return `/${tenantSlug}${path}`;
}
